import type { ThreadActionSelection } from "@afar-research/contracts";
import { BrainCircuitIcon, ChevronRightIcon, ListChecksIcon } from "lucide-react";
import { memo, type ReactNode } from "react";

import { actionIconComponent } from "./chat/actionIcons";
import type { ComposerActionEntry } from "./chat/composerActions.logic";
import type { EmptyThreadState } from "./emptyThread.logic";
import { Button } from "./ui/button";

/**
 * What an empty thread shows, following the project creation pipeline: the
 * ingest of a project created from references while it runs, then the
 * question of what to study, and otherwise the research verbs as starters
 * (open-science's WorkflowStarters). A starter sets the composer's chip; it
 * never sends anything on its own.
 */
export const EmptyThread = memo(function EmptyThread(props: {
  state: EmptyThreadState;
  verbs: ReadonlyArray<ComposerActionEntry>;
  onPickAction: (selection: ThreadActionSelection) => void;
  onOpenIngestThread: (() => void) | null;
  onOpenStudy: () => void;
}) {
  if (props.state.type === "ingesting") {
    return (
      <EmptyThreadPanel
        // A static icon: a spinner here would repaint for the whole ingest.
        icon={<BrainCircuitIcon className="size-4" />}
        eyebrow="New project"
        heading={props.state.stalled ? "The ingest stopped" : "Ingesting your references"}
        body={
          props.state.stalled
            ? "The ingest thread did not finish reading the references. Open it to retry; the project waits for it before anything else starts."
            : "The ingest thread is reading the references into the project's Brain. When it finishes, you'll be asked what this project should study."
        }
        action={
          props.onOpenIngestThread === null ? null : (
            <Button size="sm" variant="outline" onClick={props.onOpenIngestThread}>
              Open the ingest thread
            </Button>
          )
        }
      />
    );
  }
  if (props.state.type === "study") {
    return (
      <EmptyThreadPanel
        icon={<ListChecksIcon className="size-4" />}
        eyebrow="References ingested"
        heading="What should this project study?"
        body="Describe the question. A study thread starts from what the Brain now holds, sharpens the question with you, then carries it out. You can also skip it and keep just the Brain."
        action={
          <Button size="sm" onClick={props.onOpenStudy}>
            Start the study
          </Button>
        }
      />
    );
  }
  return (
    <section
      aria-labelledby="empty-thread-heading"
      className="mx-auto flex w-full max-w-[500px] flex-col px-5"
      data-empty-thread="starters"
    >
      <h2
        className="text-center font-serif text-[26px] leading-tight text-foreground"
        id="empty-thread-heading"
      >
        What should we work on?
      </h2>
      <p className="mt-2 text-center text-muted-foreground text-sm">
        Write a message, or pick a step to start from.
      </p>
      {/* Short panes keep only the invitation, so the welcome never scrolls. */}
      <div className="mt-6 overflow-hidden rounded-xl border bg-card/60 [@media(max-height:620px)]:hidden">
        {props.verbs.map((entry) => {
          const Icon = actionIconComponent(entry.icon);
          return (
            <button
              key={entry.selection.id}
              type="button"
              disabled={entry.disabledReason !== null}
              title={entry.disabledReason ?? undefined}
              onClick={() => props.onPickAction(entry.selection)}
              className="group flex w-full items-center gap-3 border-t px-4 py-2.5 text-left transition-colors first:border-t-0 hover:bg-accent/50 disabled:opacity-64 disabled:hover:bg-transparent"
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground ring-1 ring-border">
                <Icon aria-hidden="true" className="size-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-[13.5px] text-foreground">
                  {entry.label}
                </span>
                <span className="mt-0.5 block truncate text-muted-foreground text-xs">
                  {entry.disabledReason ?? entry.description}
                </span>
              </span>
              <ChevronRightIcon
                aria-hidden="true"
                className="size-4 shrink-0 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5"
              />
            </button>
          );
        })}
      </div>
    </section>
  );
});

function EmptyThreadPanel(props: {
  icon: ReactNode;
  eyebrow: string;
  heading: string;
  body: string;
  action: ReactNode;
}) {
  return (
    <section
      aria-labelledby="empty-thread-heading"
      className="mx-auto flex w-full max-w-md flex-col items-center px-6 text-center"
      data-empty-thread="pipeline"
    >
      <div className="flex size-9 items-center justify-center rounded-xl border border-border/60 bg-card/45 text-muted-foreground">
        {props.icon}
      </div>
      <p className="mt-3 font-medium text-[11px] text-muted-foreground/70 uppercase tracking-[0.14em]">
        {props.eyebrow}
      </p>
      <h2
        className="mt-1 font-serif text-[22px] leading-tight text-foreground"
        id="empty-thread-heading"
      >
        {props.heading}
      </h2>
      <p className="mt-2 text-muted-foreground text-sm leading-relaxed">{props.body}</p>
      {props.action === null ? null : <div className="mt-4">{props.action}</div>}
    </section>
  );
}
